import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import WorkIcon from "@mui/icons-material/Work";

import { useNavigate, useLocation } from "react-router-dom";
import { axiosInstance } from "../../../utils/axiosInstance";
import {
  SavedCandidateEndPoint,
  FinalShortlistedEndPoint,
  JobEndPoint,
} from "../../../api-routes";
import CommonSnackbar from "../../../components/common-components/CommonSnackbar";
import { SelectedWrapper } from "./style";
import { colors } from "../../../theme/colors";

const getScoreColor = (score) => {
  if (score >= 75) return { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" };
  if (score >= 60) return { bg: "#fffbeb", color: "#d97706", border: "#fef3c7" };
  return { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].substring(0, 2).toUpperCase();
};

const Selected = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [jobSearch, setJobSearch] = useState("");
  const [selectedJobIndex, setSelectedJobIndex] = useState(
    location.state?.selectedJobIndex ?? 0
  );
  const [loading, setLoading] = useState(true);
  const [checkedIds, setCheckedIds] = useState([]);
  const [shortlisting, setShortlisting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [candidatePage, setCandidatePage] = useState(1);
  const candidateLimit = 5;
  const [alertDetail, setAlertDetail] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [allJobs, setAllJobs] = useState([]);
  const [moving, setMoving] = useState(false);

  const fetchAllJobs = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${JobEndPoint.GET_ALL_JOBS}?limit=100`);
      setAllJobs(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch all jobs", err);
    }
  }, []);

  const handleMoveCandidate = async (event) => {
    const targetJobId = event.target.value;
    if (!targetJobId || checkedIds.length === 0) return;
    setMoving(true);
    try {
      await axiosInstance.post(SavedCandidateEndPoint.MOVE_CANDIDATES, {
        candidateIds: checkedIds,
        jobId: targetJobId,
      });
      setAlertDetail({
        open: true,
        message: `${checkedIds.length} candidate(s) successfully moved`,
        severity: "success",
      });
      setCheckedIds([]);
      fetchGroupedCandidates();
    } catch (err) {
      console.error("Failed to move candidates", err);
      setAlertDetail({
        open: true,
        message: err.response?.data?.message || "Failed to move candidates",
        severity: "error",
      });
    } finally {
      setMoving(false);
    }
  };

  const fetchGroupedCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(SavedCandidateEndPoint.GET_GROUPED);
      setJobs(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch saved candidates", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroupedCandidates();
    fetchAllJobs();
  }, [fetchGroupedCandidates, fetchAllJobs]);

  useEffect(() => {
    setCheckedIds([]);
    setStatusFilter("");
    setCandidatePage(1);
  }, [selectedJobIndex]);

  const filteredJobs = jobs.filter((j) => {
    if (!jobSearch.trim()) return true;
    const term = jobSearch.toLowerCase();
    return (
      (j.role && j.role.toLowerCase().includes(term)) ||
      (j.job_id && j.job_id.toLowerCase().includes(term))
    );
  });

  const activeJob = jobs[selectedJobIndex] || null;
  const allCandidates = activeJob?.candidates || [];

  const filteredCandidates = allCandidates.filter((c) => {
    if (!statusFilter) return true;
    if (statusFilter === "shortlisted") return c.isShortlisted;
    if (statusFilter === "notShortlisted") return !c.isShortlisted;
    return true;
  });

  const totalFilteredPages = Math.ceil(
    filteredCandidates.length / candidateLimit
  );
  const candidates = filteredCandidates.slice(
    (candidatePage - 1) * candidateLimit,
    candidatePage * candidateLimit
  );

  const selectableCandidates = filteredCandidates.filter(
    (c) => !c.isShortlisted
  );

  const handleToggle = (id) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleToggleAll = () => {
    if (checkedIds.length === selectableCandidates.length) {
      setCheckedIds([]);
    } else {
      setCheckedIds(selectableCandidates.map((c) => c._id));
    }
  };

  const handleShortlist = async () => {
    if (checkedIds.length === 0) return;
    setShortlisting(true);
    try {
      await axiosInstance.post(FinalShortlistedEndPoint.SHORTLIST, {
        candidateIds: checkedIds,
      });
      setAlertDetail({
        open: true,
        message: `${checkedIds.length} candidate(s) final shortlisted successfully`,
        severity: "success",
      });
      setCheckedIds([]);
      fetchGroupedCandidates();
    } catch (err) {
      console.error("Failed to shortlist", err);
      setAlertDetail({
        open: true,
        message:
          err.response?.data?.message || "Failed to shortlist candidates",
        severity: "error",
      });
    } finally {
      setShortlisting(false);
    }
  };

  return (
    <SelectedWrapper>
      <CommonSnackbar
        open={alertDetail.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        setOpen={setAlertDetail}
        message={alertDetail.message}
        severity={alertDetail.severity}
      />

      {/* Left Jobs Panel */}
      <Box className="jobs-panel">
        <Box className="jobs-panel__header">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WorkIcon sx={{ color: colors.primary, fontSize: 20 }} />
            <Typography className="jobs-panel__title">Saved Jobs</Typography>
          </Box>
        </Box>

        {jobs.length > 3 && (
          <Box className="jobs-panel__search">
            <TextField
              size="small"
              placeholder="Search jobs..."
              value={jobSearch}
              onChange={(e) => setJobSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "12px",
                  backgroundColor: "#f8fafc",
                },
              }}
            />
          </Box>
        )}

        <Box className="jobs-panel__list">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Box key={i} sx={{ p: 2, mb: 1 }}>
                  <Skeleton variant="text" width={80} height={16} />
                  <Skeleton variant="text" width={180} height={22} />
                  <Skeleton variant="text" width={100} height={16} />
                </Box>
              ))
            : filteredJobs.map((job) => {
                const originalIndex = jobs.findIndex(
                  (j) => j.job_id === job.job_id && j.role === job.role
                );
                const isActive = selectedJobIndex === originalIndex;

                return (
                  <Box
                    key={`${job.job_id}-${job.role}`}
                    className={`job-card ${isActive ? "job-card--active" : ""}`}
                    onClick={() => setSelectedJobIndex(originalIndex)}
                  >
                    {job.job_id && (
                      <Typography className="job-card__id">
                        {job.job_id}
                      </Typography>
                    )}
                    <Typography className="job-card__role">{job.role}</Typography>
                    <Typography className="job-card__count">
                      {job.count} Candidate{job.count !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                );
              })}
          {!loading && jobs.length === 0 && (
            <Box className="empty-state" sx={{ minHeight: "200px" }}>
              <BookmarkIcon sx={{ fontSize: 40, color: "#cbd5e1" }} />
              <Typography className="empty-state__text">
                No saved candidates yet
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Main Candidate Content Area */}
      <Box className="candidates-panel">
        {activeJob ? (
          <>
            <Box className="candidates-panel__header">
              <Box>
                <Typography className="candidates-panel__title">
                  Saved Candidates
                </Typography>
                <Typography className="candidates-panel__subtitle">
                  Job Role: <strong>{activeJob.role}</strong> {activeJob.job_id ? `(${activeJob.job_id})` : ""}
                </Typography>
              </Box>

              <Box className="candidates-panel__actions">
                <TextField
                  select
                  size="small"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCandidatePage(1);
                  }}
                  sx={{
                    minWidth: 150,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fff",
                      fontSize: "13px",
                      fontWeight: 500,
                    },
                  }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) =>
                      value === "shortlisted"
                        ? "Shortlisted"
                        : value === "notShortlisted"
                        ? "Not Shortlisted"
                        : "All Status",
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="shortlisted">Shortlisted</MenuItem>
                  <MenuItem value="notShortlisted">Not Shortlisted</MenuItem>
                </TextField>

                {checkedIds.length > 0 && (
                  <>
                    <Chip
                      label={`${checkedIds.length} Selected`}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: colors.primaryLight,
                        color: colors.primary,
                        borderRadius: "8px",
                        px: 1,
                        py: 1.5,
                      }}
                    />

                    <TextField
                      select
                      size="small"
                      value=""
                      onChange={handleMoveCandidate}
                      disabled={moving}
                      sx={{
                        minWidth: 150,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          fontSize: "13px",
                        },
                      }}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: () => (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <DriveFileMoveIcon sx={{ fontSize: 16, color: colors.primary }} />
                            Move to Job
                          </Box>
                        ),
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              "& .MuiMenuItem-root:hover": {
                                color: colors.primary,
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Move to Job
                      </MenuItem>
                      {allJobs
                        .filter((job) => job.jobId !== activeJob?.job_id)
                        .map((job) => (
                          <MenuItem key={job.jobId} value={job.jobId}>
                            {job.jobId} - {job.roleDesignation}
                          </MenuItem>
                        ))}
                    </TextField>

                    <Button
                      variant="contained"
                      startIcon={
                        shortlisting ? (
                          <CircularProgress size={16} sx={{ color: "#fff" }} />
                        ) : (
                          <StarIcon />
                        )
                      }
                      className="shortlist-btn"
                      onClick={handleShortlist}
                      disabled={shortlisting}
                    >
                      Final Shortlist ({checkedIds.length})
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            <Box className="candidates-table-wrapper">
              <TableContainer className="candidates-table">
                <Table sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ width: 48, padding: "8px 12px" }}>
                        <Checkbox
                          size="small"
                          checked={
                            selectableCandidates.length > 0 &&
                            checkedIds.length === selectableCandidates.length
                          }
                          indeterminate={
                            checkedIds.length > 0 &&
                            checkedIds.length < selectableCandidates.length
                          }
                          onChange={handleToggleAll}
                          disabled={selectableCandidates.length === 0}
                          sx={{
                            color: "rgba(255,255,255,0.7)",
                            "&.Mui-checked": { color: "#fff" },
                            "&.MuiCheckbox-indeterminate": { color: "#fff" },
                          }}
                        />
                      </TableCell>
                      <TableCell align="left" sx={{ textAlign: "left", pl: 3 }}>
                        CANDIDATE
                      </TableCell>
                      <TableCell align="center">CURRENT CTC</TableCell>
                      <TableCell align="center">EXPECTED CTC</TableCell>
                      <TableCell align="center">YOE</TableCell>
                      <TableCell align="center">MATCH SCORE</TableCell>
                      <TableCell align="center">STATUS</TableCell>
                      <TableCell align="center">ACTION</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidates.length > 0 ? (
                      candidates.map((candidate) => {
                        const scoreMeta = getScoreColor(candidate.matchScore);
                        const isShortlisted = candidate.isShortlisted;
                        return (
                          <TableRow
                            key={candidate._id}
                            className={isShortlisted ? "row--shortlisted" : ""}
                            sx={{
                              cursor: isShortlisted ? "default" : "pointer",
                            }}
                            onClick={() => {
                              if (!isShortlisted) {
                                navigate(
                                  `/app/selected/candidate/${candidate._id}`,
                                  { state: { selectedJobIndex } }
                                );
                              }
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{ padding: "8px 12px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                size="small"
                                checked={checkedIds.includes(candidate._id)}
                                onChange={() => handleToggle(candidate._id)}
                                disabled={isShortlisted}
                                sx={{
                                  color: colors.primary,
                                  "&.Mui-checked": { color: colors.primary },
                                  "&.Mui-disabled": { color: "#cbd5e1" },
                                }}
                              />
                            </TableCell>
                            <TableCell align="left" sx={{ textAlign: "left", pl: 3 }}>
                              <Box className="table_user-primary-detail">
                                <Box className="candidate-avatar">
                                  {getInitials(candidate.candidateName)}
                                </Box>
                                <Box sx={{ textAlign: "left" }}>
                                  <Typography className="candidate-name">
                                    {candidate.candidateName}
                                  </Typography>
                                  {candidate.email && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "left" }}>
                                      {candidate.email}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>

                            <TableCell align="center">
                              {candidate.currentCTC != null
                                ? `${Number(candidate.currentCTC)} LPA`
                                : "—"}
                            </TableCell>

                            <TableCell align="center">
                              {candidate.expectedCTC != null
                                ? `${Number(candidate.expectedCTC)} LPA`
                                : "—"}
                            </TableCell>

                            <TableCell align="center">
                              {candidate.totalExp != null
                                ? `${candidate.totalExp} Yrs`
                                : "—"}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${candidate.matchScore}%`}
                                size="small"
                                className="score-chip"
                                sx={{
                                  backgroundColor: scoreMeta.bg,
                                  color: scoreMeta.color,
                                  border: `1px solid ${scoreMeta.border}`,
                                  fontWeight: 700,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {isShortlisted ? (
                                <Chip
                                  icon={
                                    <CheckCircleIcon
                                      sx={{
                                        fontSize: 16,
                                        color: "#15803d !important",
                                      }}
                                    />
                                  }
                                  label="Shortlisted"
                                  size="small"
                                  className="shortlisted-chip"
                                />
                              ) : (
                                <Typography
                                  sx={{ fontSize: "13px", color: "#94a3b8" }}
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="small"
                                variant="contained"
                                className="schedule-btn"
                                startIcon={<CalendarMonthIcon sx={{ fontSize: 15 }} />}
                                onClick={() => {
                                  navigate(
                                    "/app/interview/schedule-interview",
                                    {
                                      state: {
                                        candidateName: candidate.candidateName,
                                        candidateEmail: candidate.email || "",
                                        job_id: activeJob?.job_id || "",
                                        role: activeJob?.role || "",
                                        sessionId: candidate.sessionId || "",
                                      },
                                    }
                                  );
                                }}
                              >
                                Schedule Interview
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                            <BookmarkIcon sx={{ fontSize: 56, color: "#cbd5e1" }} />
                            <Typography variant="h6" fontWeight={600} color="text.secondary">
                              No saved candidates found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              There are no saved candidates matching this filter.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Pagination Footer */}
            <Box className="pagination-bar">
              <Typography className="pagination-info">
                Showing{" "}
                {filteredCandidates.length > 0
                  ? (candidatePage - 1) * candidateLimit + 1
                  : 0}{" "}
                to{" "}
                {Math.min(
                  candidatePage * candidateLimit,
                  filteredCandidates.length
                )}{" "}
                of {filteredCandidates.length} entries
              </Typography>
              <Box className="pagination-buttons">
                <Button
                  variant="contained"
                  className="pagination-btn"
                  onClick={() => setCandidatePage((p) => p - 1)}
                  disabled={candidatePage === 1}
                  startIcon={<NavigateBeforeIcon />}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  className="pagination-btn"
                  onClick={() => setCandidatePage((p) => p + 1)}
                  disabled={candidatePage >= totalFilteredPages || totalFilteredPages === 0}
                  endIcon={<NavigateNextIcon />}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <Box className="empty-state">
            <BookmarkIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
            <Typography className="empty-state__text">
              {loading ? "Loading saved candidates..." : "Select a job to view saved candidates"}
            </Typography>
          </Box>
        )}
      </Box>
    </SelectedWrapper>
  );
};

export default Selected;
